// src/types/child.ts
// Child-related type definitions for BrightPath

import { z } from 'zod';

// ============================================================================
// Enums & Constants
// ============================================================================

export const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non-binary', label: 'Non-Binary' },
  { value: 'other', label: 'Other' },
  { value: 'prefer-not-to-say', label: 'Prefer not to say' },
] as const;

export const DELIVERY_TYPE_OPTIONS = [
  { value: 'vaginal', label: 'Vaginal Delivery' },
  { value: 'cesarean', label: 'Cesarean Section (C-Section)' },
  { value: 'vacuum-assisted', label: 'Vacuum-Assisted Delivery' },
  { value: 'forceps-assisted', label: 'Forceps-Assisted Delivery' },
  { value: 'unknown', label: 'Unknown' },
] as const;

export const BLOOD_TYPE_OPTIONS = [
  { value: 'A+', label: 'A+' },
  { value: 'A-', label: 'A-' },
  { value: 'B+', label: 'B+' },
  { value: 'B-', label: 'B-' },
  { value: 'AB+', label: 'AB+' },
  { value: 'AB-', label: 'AB-' },
  { value: 'O+', label: 'O+' },
  { value: 'O-', label: 'O-' },
  { value: 'unknown', label: 'Unknown' },
] as const;

// ============================================================================
// Zod Schemas for Validation
// ============================================================================

export const childFormSchema = z.object({
  first_name: z
    .string()
    .min(1, 'First name is required')
    .max(100, 'First name must be less than 100 characters'),
  last_name: z
    .string()
    .min(1, 'Last name is required')
    .max(100, 'Last name must be less than 100 characters'),
  nickname: z
    .string()
    .max(50, 'Nickname must be less than 50 characters')
    .optional()
    .nullable(),
  date_of_birth: z.string().min(1, 'Date of birth is required'),
  gender: z.string().optional().nullable(),
  birth_weight_oz: z.coerce.number().positive().optional().nullable(),
  birth_length_inches: z.coerce.number().positive().optional().nullable(),
  gestational_age_weeks: z.coerce
    .number()
    .min(20, 'Gestational age must be at least 20 weeks')
    .max(45, 'Gestational age must be at most 45 weeks')
    .optional()
    .nullable(),
  birth_hospital: z.string().max(255).optional().nullable(),
  birth_city: z.string().max(100).optional().nullable(),
  birth_state: z.string().max(50).optional().nullable(),
  delivery_type: z.string().optional().nullable(),
  blood_type: z.string().optional().nullable(),
  allergies: z.array(z.string()).optional().default([]),
  insurance_provider: z.string().max(200).optional().nullable(),
  insurance_policy_number: z.string().max(100).optional().nullable(),
  medicaid_id: z.string().max(100).optional().nullable(),
  notes: z.string().optional().nullable(),
});

export type ChildFormData = z.infer<typeof childFormSchema>;

// ============================================================================
// API Response Types
// ============================================================================

export interface DiagnosisInfo {
  id: string;
  name: string;
  short_name?: string;
  slug: string;
  icon_url?: string;
}

export interface ChildDiagnosis {
  id: string;
  child_id: string;
  diagnosis_id: string;
  diagnosis: DiagnosisInfo;
  diagnosed_date?: string;
  diagnosed_by?: string;
  severity?: string;
  subtype?: string;
  classification_level?: string;
  is_primary: boolean;
  notes?: string;
  created_at: string;
}

export interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
  is_primary: boolean;
}

export interface EmergencyInfo {
  contacts: EmergencyContact[];
  hospital_preference?: string;
  special_instructions?: string;
}

export interface Medication {
  id: string;
  name: string;
  generic_name?: string;
  dosage: string;
  frequency: string;
  route?: string;
  prescribing_doctor?: string;
  pharmacy?: string;
  rx_number?: string;
  start_date?: string;
  end_date?: string;
  instructions?: string;
  side_effects?: string;
  is_active: boolean;
  reminder_enabled: boolean;
  reminder_times?: string[];
}

export interface Child {
  id: string;
  family_id: string;
  first_name: string;
  last_name: string;
  nickname?: string;
  date_of_birth: string;
  gender?: string;
  avatar_url?: string;
  birth_weight_oz?: number;
  birth_length_inches?: number;
  gestational_age_weeks?: number;
  birth_hospital?: string;
  birth_city?: string;
  birth_state?: string;
  delivery_type?: string;
  blood_type?: string;
  allergies: string[];
  current_medications: Medication[];
  insurance_provider?: string;
  insurance_policy_number?: string;
  medicaid_id?: string;
  emergency_info?: EmergencyInfo;
  notes?: string;
  created_at: string;
  updated_at: string;
  // Computed fields
  diagnoses?: ChildDiagnosis[];
  age_years?: number;
  age_months?: number;
  full_name?: string;
}

export interface ChildListResponse {
  items: Child[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface ChildCreateRequest {
  first_name: string;
  last_name: string;
  nickname?: string;
  date_of_birth: string;
  gender?: string;
  birth_weight_oz?: number;
  birth_length_inches?: number;
  gestational_age_weeks?: number;
  birth_hospital?: string;
  birth_city?: string;
  birth_state?: string;
  delivery_type?: string;
  blood_type?: string;
  allergies?: string[];
  insurance_provider?: string;
  insurance_policy_number?: string;
  medicaid_id?: string;
  notes?: string;
}

export interface ChildUpdateRequest extends Partial<ChildCreateRequest> {
  avatar_url?: string;
  emergency_info?: EmergencyInfo;
}

// ============================================================================
// Filter & Search Types
// ============================================================================

export interface ChildFilters {
  search?: string;
  diagnosis_id?: string;
  age_min?: number;
  age_max?: number;
  sort_by?: 'name' | 'age' | 'created_at' | 'updated_at';
  sort_order?: 'asc' | 'desc';
}

export interface ChildListParams extends ChildFilters {
  page?: number;
  page_size?: number;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Calculate age from date of birth
 */
export function calculateAge(dateOfBirth: string): { years: number; months: number } {
  const birth = new Date(dateOfBirth);
  const today = new Date();
  
  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();
  
  if (months < 0 || (months === 0 && today.getDate() < birth.getDate())) {
    years--;
    months += 12;
  }
  
  if (today.getDate() < birth.getDate()) {
    months--;
    if (months < 0) months += 12;
  }
  
  return { years, months };
}

/**
 * Format age for display
 */
export function formatAge(dateOfBirth: string): string {
  const { years, months } = calculateAge(dateOfBirth);
  
  if (years === 0) {
    return `${months} month${months !== 1 ? 's' : ''} old`;
  }
  
  if (years < 2) {
    return `${years} year${years !== 1 ? 's' : ''}, ${months} month${months !== 1 ? 's' : ''} old`;
  }
  
  return `${years} years old`;
}

/**
 * Get child's full name
 */
export function getChildFullName(child: Pick<Child, 'first_name' | 'last_name' | 'nickname'>): string {
  const fullName = `${child.first_name} ${child.last_name}`;
  return child.nickname ? `${fullName} (${child.nickname})` : fullName;
}

/**
 * Get child's initials for avatar
 */
export function getChildInitials(child: Pick<Child, 'first_name' | 'last_name'>): string {
  return `${child.first_name.charAt(0)}${child.last_name.charAt(0)}`.toUpperCase();
}

/**
 * Get primary diagnosis name
 */
export function getPrimaryDiagnosis(child: Child): string | null {
  const primary = child.diagnoses?.find(d => d.is_primary);
  return primary?.diagnosis.name ?? child.diagnoses?.[0]?.diagnosis.name ?? null;
}

/**
 * Convert weight in ounces to pounds and ounces
 */
export function formatBirthWeight(weightOz: number): string {
  const pounds = Math.floor(weightOz / 16);
  const ounces = weightOz % 16;
  return `${pounds} lb ${ounces} oz`;
}

/**
 * Get avatar gradient based on child's name
 */
export function getAvatarGradient(name: string): string {
  const gradients = [
    'linear-gradient(135deg, #7A9E7E 0%, #5C7D60 100%)', // Sage
    'linear-gradient(135deg, #C4785A 0%, #D99B7E 100%)', // Terracotta
    'linear-gradient(135deg, #6B8DD6 0%, #95AEED 100%)', // Blue
    'linear-gradient(135deg, #2B3A4D 0%, #445566 100%)', // Navy
    'linear-gradient(135deg, #9DB8A0 0%, #7A9E7E 100%)', // Light Sage
    'linear-gradient(135deg, #D99B7E 0%, #C4785A 100%)', // Light Terracotta
  ];
  
  // Simple hash based on name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return gradients[Math.abs(hash) % gradients.length];
}
